import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBrain, FiTarget, FiZap } = FiIcons;

const SolutionSection = () => {
  const skillCategories = [
    {
      icon: FiBrain,
      title: "Root Skills",
      subtitle: "Mental Foundation",
      color: "accent-magenta",
      bgColor: "bg-accent-magenta/10",
      borderColor: "border-accent-magenta/20",
      skills: [
        "Attention regulation: Lock-in focus that doesn't waver under pressure",
        "Working memory training: Track multiple game elements at once", 
        "Inhibition control: Stop overthinking and trust your training",
        "Interoception: Read your body's signals for optimal performance timing"
      ]
    },
    {
      icon: FiTarget,
      title: "Leaf Skills", 
      subtitle: "Game Application",
      color: "accent-gold",
      bgColor: "bg-accent-gold/10",
      borderColor: "border-accent-gold/20",
      skills: [
        "Self-monitoring: Catch mental mistakes before they become physical ones",
        "Time sense: Perfect timing and rhythm in dynamic play",
        "Cognitive flexibility: Shift tactics rapidly without losing momentum",
        "Emotional regulation: Channel adrenaline into performance fuel"
      ]
    },
    {
      icon: FiZap,
      title: "Flower Skills",
      subtitle: "Championship Mastery", 
      color: "accent-purple",
      bgColor: "bg-accent-purple/10",
      borderColor: "border-accent-purple/20",
      skills: [
        "Tactical decision-making: Make the right call in split seconds",
        "Anticipation: Predict opponents' moves before they happen; always be 3 steps ahead",
        "Pressure management: Perform better when stakes are highest", 
        "Mental resilience: Bounce back instantly from setbacks"
      ]
    }
  ];

  return (
    <section id="solution" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Train Your Brain Like You Train Your Body
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-xl text-gray-700 leading-relaxed">
              Forget generic sports psychology. We're talking performance neuroscience.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              Thousands of hours go into physical training—how much into brain training?
            </p>
            
            <p className="text-lg text-gray-800 font-medium">
              We build the mental infrastructure that separates players and athletes from champions.
            </p>
          </div>
        </motion.div>

        {/* System Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="bg-gradient-to-r from-accent-light/50 to-accent-purple/30 p-8 rounded-2xl border border-accent-lavender/30">
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Our 3-Level Championship Mindset System:
            </h3>
          </div>
        </motion.div>

        {/* Skills Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {skillCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`${category.bgColor} ${category.borderColor} border-2 rounded-2xl p-8 hover:shadow-lg transition-all duration-300`}
            >
              {/* Header */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4`}>
                  <SafeIcon icon={category.icon} className={`h-8 w-8 text-${category.color}`} />
                </div>
                
                <h4 className="font-playfair text-xl font-bold text-gray-900 mb-2">
                  {category.title}
                </h4>
                
                <p className={`text-${category.color} font-semibold`}>
                  {category.subtitle}
                </p>
              </div>

              {/* Skills List */}
              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skillIndex}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: (index * 0.2) + (skillIndex * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <div className={`w-2 h-2 bg-${category.color} rounded-full mt-2 flex-shrink-0`}></div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {skill}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary text-lg"
          >
            Start Your Mental Training
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;